import * as S from './Unauthenticated.styles'

import MediaMatch from '../mediaMatch/MediaMatch'
import loginVideo from '../../assets/video/login-video.mp4'
import { ReactNode, useEffect, useRef } from 'react'

type UnauthenticatedTemplateProps = {
  children: ReactNode
  image: string
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number
) {
  const vw = video.videoWidth
  const vh = video.videoHeight
  if (!vw || !vh) return

  const scale = Math.max(width / vw, height / vh)
  const dw = vw * scale
  const dh = vh * scale
  const dx = (width - dw) / 2
  const dy = (height - dh) / 2

  ctx.clearRect(0, 0, width, height)
  ctx.drawImage(video, dx, dy, dw, dh)
}

export default function UnauthenticatedTemplate({
  children,
  image
}: UnauthenticatedTemplateProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const video = document.createElement('video')
    video.muted = true
    video.defaultMuted = true
    video.loop = true
    video.playsInline = true
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', 'true')
    video.preload = 'auto'
    video.src = loginVideo
    video.setAttribute('aria-hidden', 'true')
    video.tabIndex = -1
    // Decoder stays in-viewport but invisible so mobile browsers still decode.
    video.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;opacity:0;pointer-events:none;z-index:-2'
    document.body.appendChild(video)

    let rafId = 0
    let disposed = false
    let cssW = 0
    let cssH = 0

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      cssW = window.innerWidth
      cssH = window.innerHeight
      // Cap bitmap to avoid 4K canvas OOM on mobile.
      const maxSide = 1920
      const scale = Math.min(dpr, maxSide / Math.max(cssW, cssH, 1))
      const bw = Math.max(1, Math.floor(cssW * scale))
      const bh = Math.max(1, Math.floor(cssH * scale))
      if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw
        canvas.height = bh
      }
      canvas.style.width = `${cssW}px`
      canvas.style.height = `${cssH}px`
      ctx.setTransform(bw / cssW, 0, 0, bh / cssH, 0, 0)
    }

    const paint = () => {
      if (disposed) return
      if (video.readyState >= 2 && cssW > 0 && cssH > 0) {
        drawCover(ctx, video, cssW, cssH)
      }
      rafId = window.requestAnimationFrame(paint)
    }

    const tryPlay = () => {
      const playPromise = video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    }

    const onGesturePlay = () => tryPlay()

    resize()
    paint()
    tryPlay()

    video.addEventListener('loadeddata', tryPlay)
    video.addEventListener('loadedmetadata', tryPlay)
    window.addEventListener('resize', resize)
    window.addEventListener('orientationchange', resize)
    document.addEventListener('touchstart', onGesturePlay, {
      once: true,
      passive: true
    })
    document.addEventListener('click', onGesturePlay, { once: true })

    return () => {
      disposed = true
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('resize', resize)
      window.removeEventListener('orientationchange', resize)
      document.removeEventListener('touchstart', onGesturePlay)
      document.removeEventListener('click', onGesturePlay)
      video.removeEventListener('loadeddata', tryPlay)
      video.removeEventListener('loadedmetadata', tryPlay)
      video.pause()
      video.removeAttribute('src')
      video.load()
      video.remove()
    }
  }, [])

  return (
    <S.Container>
      <S.BackgroundCanvas ref={canvasRef} aria-hidden />
      <MediaMatch greaterThan="large">
        <S.TemplateImage>
          <S.Image src={image} />
        </S.TemplateImage>
      </MediaMatch>
      <S.TemplatePage>{children}</S.TemplatePage>
    </S.Container>
  )
}
